import { useState, useEffect, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers },
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

// ── Constants ─────────────────────────────────────────────────
const ROLES = [
  { id:"all",     label:"Everyone",  color:"#6b7280", bg:"#f3f4f6" },
  { id:"admin",   label:"Admin",     color:"#dc2626", bg:"#fef2f2" },
  { id:"manager", label:"Manager",   color:"#2563eb", bg:"#eff6ff" },
  { id:"staff",   label:"Staff",     color:"#15803d", bg:"#f0fdf4" },
];

const PRIORITIES = [
  { id:"urgent", label:"🔴 Urgent", color:"#dc2626", bg:"#fef2f2" },
  { id:"high",   label:"🟠 High",   color:"#ea580c", bg:"#fff7ed" },
  { id:"medium", label:"🟡 Medium", color:"#d97706", bg:"#fffbeb" },
  { id:"low",    label:"🟢 Low",    color:"#15803d", bg:"#f0fdf4" },
];

const TYPES = [
  { id:"daily",   label:"🔁 Daily",   desc:"Repeats every day" },
  { id:"weekly",  label:"📅 Weekly",  desc:"Repeats on a specific day" },
  { id:"monthly", label:"🗓️ Monthly", desc:"Repeats on a date each month" },
  { id:"once",    label:"1️⃣ One-time", desc:"Single task with deadline" },
];

const DAYS    = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const DATES   = Array.from({length:31},(_,i)=>String(i+1));
const DEPARTMENTS = [
  { id:"finance",     label:"Finance & Accounting",       icon:"💰", color:"#15803d" },
  { id:"marketing",   label:"Marketing",                  icon:"📣", color:"#ec4899" },
  { id:"website",     label:"Website Development",        icon:"🌐", color:"#2563eb" },
  { id:"todo",        label:"To Do List",                 icon:"✅", color:"#F47920"  },
  { id:"customer",    label:"Customer Service",           icon:"🎧", color:"#0ea5e9" },
  { id:"sales",       label:"Sales",                      icon:"📈", color:"#7c3aed" },
  { id:"social",      label:"Social Media Content",       icon:"📱", color:"#db2777" },
  { id:"marketplace", label:"Marketplace Manager",        icon:"🛒", color:"#c45000" },
  { id:"socialmgr",   label:"Social Media Manager",       icon:"💬", color:"#9f2089" },
  { id:"merch",       label:"Merchandiser",               icon:"🧵", color:"#a16207" },
  { id:"logistics",   label:"Logistics & Fulfillment",    icon:"🚚", color:"#0369a1" },
  { id:"product",     label:"Product Manager",            icon:"📦", color:"#1A3A5C" },
  { id:"hr",          label:"Human Resource",             icon:"👥", color:"#6366f1" },
  { id:"general",     label:"General",                    icon:"📝", color:"#6b7280" },
];
const CATS = DEPARTMENTS.map((d) => d.label);
const STATUSES= [
  { id:"pending",     label:"Pending",     color:"#6b7280", bg:"#f3f4f6" },
  { id:"in_progress", label:"In Progress", color:"#2563eb", bg:"#eff6ff" },
  { id:"done",        label:"Done",        color:"#15803d", bg:"#f0fdf4" },
  { id:"cancelled",   label:"Cancelled",   color:"#dc2626", bg:"#fef2f2" },
];

const BLANK = {
  title:"", description:"", type:"daily", priority:"medium",
  assignedRole:"all", assignedTo:"", dueDate:"", dueTime:"",
  repeatDay:"", category:"General", checklist:[], tags:[],
};

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  input:   { width:"100%", padding:"8px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  th:      { padding:"9px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", fontSize:12, verticalAlign:"middle" },
};

const Badge = ({ label, color, bg, size=11 }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:size, fontWeight:600, whiteSpace:"nowrap" }}>{label}</span>
);

function priorityInfo(id) { return PRIORITIES.find((p)=>p.id===id)||PRIORITIES[2]; }
function roleInfo(id)     { return ROLES.find((r)=>r.id===id)||ROLES[0]; }
function statusInfo(id)   { return STATUSES.find((s)=>s.id===id)||STATUSES[0]; }

function isOverdue(task) {
  if (task.status==="done"||task.status==="cancelled") return false;
  if (!task.dueDate) return false;
  return new Date(task.dueDate) < new Date(new Date().setHours(0,0,0,0));
}

// ── Task Card (kanban / list) ────────────────────────────────
function TaskCard({ task, onEdit, onStatusChange, onDelete, onCheckItem, compact=false }) {
  const pri  = priorityInfo(task.priority);
  const role = roleInfo(task.assignedRole);
  const st   = statusInfo(task.status);
  const over = isOverdue(task);
  const done = task.checklist.filter((c)=>c.done).length;
  const total= task.checklist.length;

  return (
    <div style={{ background:"#fff", borderRadius:9, border:"1px solid "+(over?"#fca5a5":"#e5e7eb"), borderLeft:"4px solid "+pri.color, padding:"12px 14px", marginBottom:10, boxShadow:"0 1px 3px rgba(0,0,0,0.04)" }}>
      {/* top row */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          <Badge label={pri.label} color={pri.color} bg={pri.bg}/>
          <Badge label={TYPES.find((t)=>t.id===task.type)?.label||task.type} color="#6b7280" bg="#f3f4f6"/>
          {task.category && task.category!=="General" && <Badge label={task.category} color="#6366f1" bg="#ede9fe"/>}
          {over && <Badge label="⚠️ Overdue" color="#dc2626" bg="#fef2f2"/>}
        </div>
        <div style={{ display:"flex", gap:5 }}>
          <button style={{ ...S.btnOut, padding:"2px 7px", fontSize:10 }} onClick={()=>onEdit(task)}>Edit</button>
          <button style={{ ...S.btnOut, padding:"2px 7px", fontSize:10, color:"#dc2626", borderColor:"#fecaca" }} onClick={()=>onDelete(task._id)}>Del</button>
        </div>
      </div>

      {/* title */}
      <div style={{ fontWeight:600, fontSize:13, marginBottom:4, textDecoration:task.status==="done"?"line-through":"none", color:task.status==="done"?"#9ca3af":"#111" }}>
        {task.title}
      </div>
      {task.description && <div style={{ fontSize:11, color:"#6b7280", marginBottom:6, lineHeight:1.4 }}>{task.description}</div>}

      {/* checklist */}
      {task.checklist.length>0 && (
        <div style={{ marginBottom:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
            <div style={{ flex:1, background:"#f3f4f6", borderRadius:10, height:4 }}>
              <div style={{ background:"#22c55e", borderRadius:10, height:4, width:(total?done/total*100:0)+"%" }}/>
            </div>
            <span style={{ fontSize:10, color:"#6b7280" }}>{done}/{total}</span>
          </div>
          {task.checklist.map((c,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:7, marginBottom:2, cursor:"pointer" }} onClick={()=>onCheckItem(task._id,i)}>
              <div style={{ width:14, height:14, borderRadius:3, border:"1.5px solid "+(c.done?"#22c55e":"#d1d5db"), background:c.done?"#22c55e":"#fff", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                {c.done && <span style={{ fontSize:9, color:"#fff", fontWeight:900 }}>✓</span>}
              </div>
              <span style={{ fontSize:11, color:c.done?"#9ca3af":"#374151", textDecoration:c.done?"line-through":"none" }}>{c.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* bottom row */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:6, marginTop:6 }}>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          <Badge label={role.label} color={role.color} bg={role.bg} size={10}/>
          {task.assignedTo && <span style={{ fontSize:10, color:"#6b7280" }}>→ {task.assignedTo}</span>}
          {task.dueDate && (
            <span style={{ fontSize:10, color:over?"#dc2626":"#6b7280" }}>
              📅 {new Date(task.dueDate).toLocaleDateString("en-IN")} {task.dueTime}
            </span>
          )}
        </div>
        <select value={task.status} onChange={(e)=>onStatusChange(task._id, e.target.value)}
          style={{ fontSize:10, padding:"2px 6px", borderRadius:5, border:"1px solid #d1d5db", fontFamily:"inherit", cursor:"pointer", background:st.bg, color:st.color, fontWeight:600 }}>
          {STATUSES.map((s)=><option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>
    </div>
  );
}

// ── Add/Edit Modal ────────────────────────────────────────────
function TaskModal({ task, members, onSave, onClose, toast }) {
  const [form,       setForm]       = useState(task ? { ...BLANK, ...task, dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split("T")[0] : "", checklist: task.checklist?.map((c)=>c.text)||[] } : { ...BLANK });
  const [checkInput, setCheckInput] = useState("");
  const [saving,     setSaving]     = useState(false);
  const set = (k,v) => setForm((f)=>({...f,[k]:v}));

  const addCheck = () => {
    if (!checkInput.trim()) return;
    setForm((f)=>({...f, checklist:[...f.checklist, checkInput.trim()]}));
    setCheckInput("");
  };

  const removeCheck = (i) => setForm((f)=>({...f, checklist:f.checklist.filter((_,idx)=>idx!==i)}));

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000, padding:16 }}>
      <div style={{ background:"#fff", borderRadius:12, width:"100%", maxWidth:580, maxHeight:"92vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.25)" }}>
        <div style={{ padding:"14px 18px", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:700, fontSize:15 }}>{task?"Edit Task":"New Task"}</span>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#6b7280" }}>×</button>
        </div>
        <div style={{ padding:18, display:"flex", flexDirection:"column", gap:13 }}>

          {/* title */}
          <div>
            <label style={S.label}>Task Title *</label>
            <input style={S.input} placeholder="e.g. Check pending Shopify orders" value={form.title} onChange={(e)=>set("title",e.target.value)}/>
          </div>
          <div>
            <label style={S.label}>Description</label>
            <textarea style={{ ...S.input, height:58, resize:"vertical" }} placeholder="Details, steps, links…" value={form.description} onChange={(e)=>set("description",e.target.value)}/>
          </div>

          {/* type selector */}
          <div>
            <label style={S.label}>Task Type *</label>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8 }}>
              {TYPES.map((t)=>(
                <button key={t.id} onClick={()=>set("type",t.id)} style={{ padding:"8px 6px", borderRadius:7, border:"2px solid "+(form.type===t.id?"#F47920":"#e5e7eb"), background:form.type===t.id?"#fff8f2":"#fff", cursor:"pointer", fontFamily:"inherit", textAlign:"center" }}>
                  <div style={{ fontSize:13, marginBottom:2 }}>{t.label.split(" ")[0]}</div>
                  <div style={{ fontSize:10, fontWeight:600, color:form.type===t.id?"#F47920":"#374151" }}>{t.label.split(" ").slice(1).join(" ")}</div>
                  <div style={{ fontSize:9, color:"#9ca3af", marginTop:2 }}>{t.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* repeat day for weekly/monthly */}
          {form.type==="weekly" && (
            <div>
              <label style={S.label}>Repeat on Day</label>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {DAYS.map((d)=>(
                  <button key={d} onClick={()=>set("repeatDay",d)} style={{ padding:"5px 10px", borderRadius:6, border:"1.5px solid "+(form.repeatDay===d?"#F47920":"#e5e7eb"), background:form.repeatDay===d?"#fff8f2":"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:form.repeatDay===d?700:400, color:form.repeatDay===d?"#F47920":"#374151", textTransform:"capitalize" }}>{d.slice(0,3)}</button>
                ))}
              </div>
            </div>
          )}
          {form.type==="monthly" && (
            <div>
              <label style={S.label}>Repeat on Date of Month</label>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {DATES.map((d)=>(
                  <button key={d} onClick={()=>set("repeatDay",d)} style={{ width:32, height:32, borderRadius:6, border:"1.5px solid "+(form.repeatDay===d?"#F47920":"#e5e7eb"), background:form.repeatDay===d?"#F47920":"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, color:form.repeatDay===d?"#fff":"#374151" }}>{d}</button>
                ))}
              </div>
            </div>
          )}
          {form.type==="once" && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
              <div>
                <label style={S.label}>Due Date</label>
                <input type="date" style={S.input} value={form.dueDate} onChange={(e)=>set("dueDate",e.target.value)}/>
              </div>
              <div>
                <label style={S.label}>Due Time (optional)</label>
                <input type="time" style={S.input} value={form.dueTime} onChange={(e)=>set("dueTime",e.target.value)}/>
              </div>
            </div>
          )}

          {/* priority + category */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <label style={S.label}>Priority</label>
              <div style={{ display:"flex", gap:5 }}>
                {PRIORITIES.map((p)=>(
                  <button key={p.id} onClick={()=>set("priority",p.id)} style={{ flex:1, padding:"5px 4px", borderRadius:6, border:"1.5px solid "+(form.priority===p.id?p.color:"#e5e7eb"), background:form.priority===p.id?p.bg:"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:10, fontWeight:700, color:p.color }}>{p.label.split(" ")[0]}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={S.label}>Category</label>
              <select style={S.input} value={form.category} onChange={(e)=>set("category",e.target.value)}>
                {CATS.map((c)=><option key={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* assign */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div>
              <label style={S.label}>Assign to Role</label>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                {ROLES.map((r)=>(
                  <button key={r.id} onClick={()=>set("assignedRole",r.id)} style={{ padding:"4px 10px", borderRadius:6, border:"1.5px solid "+(form.assignedRole===r.id?r.color:"#e5e7eb"), background:form.assignedRole===r.id?r.bg:"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:11, fontWeight:700, color:r.color }}>{r.label}</button>
                ))}
              </div>
            </div>
            <div>
              <label style={S.label}>Assign to Person (optional)</label>
              <input style={S.input} placeholder="Name or leave blank for role" value={form.assignedTo} onChange={(e)=>set("assignedTo",e.target.value)} list="member-list"/>
              <datalist id="member-list">
                {(members||[]).map((m)=><option key={m._id} value={m.name}/>)}
              </datalist>
            </div>
          </div>

          {/* checklist */}
          <div>
            <label style={S.label}>Checklist / Subtasks</label>
            {form.checklist.map((c,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <div style={{ width:14, height:14, borderRadius:3, border:"1.5px solid #d1d5db", flexShrink:0 }}/>
                <span style={{ fontSize:12, flex:1 }}>{c}</span>
                <button onClick={()=>removeCheck(i)} style={{ background:"none", border:"none", color:"#dc2626", cursor:"pointer", fontSize:14 }}>×</button>
              </div>
            ))}
            <div style={{ display:"flex", gap:8, marginTop:4 }}>
              <input style={{ ...S.input, flex:1 }} placeholder="Add checklist item…" value={checkInput} onChange={(e)=>setCheckInput(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&(e.preventDefault(),addCheck())}/>
              <button style={{ ...S.btn("#6366f1"), padding:"7px 12px", fontSize:12 }} onClick={addCheck}>+ Add</button>
            </div>
          </div>

          <div style={{ display:"flex", gap:10, justifyContent:"flex-end", marginTop:4 }}>
            <button style={S.btnOut} onClick={onClose}>Cancel</button>
            <button style={S.btn()} disabled={saving} onClick={async()=>{
              if(!form.title.trim()){toast("Enter task title","error");return;}
              setSaving(true);
              try { await onSave(form); onClose(); }
              catch(e){ toast(e.message,"error"); }
              setSaving(false);
            }}>{saving?"Saving…":task?"Save Changes":"Create Task"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════

// ── Department Panel — separate component avoids IIFE crash ──
function DeptPanel({ filterCat, filterStatus, setFilterStatus, search, setSearch, tasks, members, openAdd, openEdit, changeStatus, deleteTask, checkItem, setEditTask, setShowModal }) {
  // Safe dept lookup - always fall back to first dept
  const safeCat   = (!filterCat || filterCat === "all") ? DEPARTMENTS[0].label : filterCat;
  const dept      = DEPARTMENTS.find((d) => d.label === safeCat) || DEPARTMENTS[0];
  const deptColor = dept?.color || "#F47920";
  const deptIcon  = dept?.icon  || "📋";
  const deptLabel = dept?.label || "All Tasks";
  const deptTasks = tasks.filter((t) => !safeCat || safeCat === "all" ? true : t.category === safeCat);
  const pending   = deptTasks.filter((t) => t.status === "pending" || t.status === "in_progress");
  const done      = deptTasks.filter((t) => t.status === "done");
  const filtered  = deptTasks.filter((t) => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (search) { const q = search.toLowerCase(); return t.title.toLowerCase().includes(q) || (t.description||"").toLowerCase().includes(q); }
    return true;
  });

  const S2 = {
    btn: (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
    btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  };

  return (
    <div style={{ flex:1, minWidth:0 }}>
      {/* Dept header */}
      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px", marginBottom:12, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:36, height:36, borderRadius:8, background:deptColor+"18", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{deptIcon}</div>
          <div>
            <div style={{ fontWeight:700, fontSize:15, color:deptColor }}>{deptLabel}</div>
            <div style={{ fontSize:11, color:"#9ca3af" }}>{pending.length} pending · {done.length} completed</div>
          </div>
        </div>
        <button style={S2.btn(deptColor)} onClick={() => { setEditTask(null); setShowModal(true); }}>+ Add Task</button>
      </div>

      {/* Status filters + search */}
      <div style={{ display:"flex", gap:6, marginBottom:12, flexWrap:"wrap" }}>
        {[["all","All"],["pending","Pending"],["in_progress","In Progress"],["done","Done"]].map(([v,l]) => (
          <button key={v} onClick={() => setFilterStatus(v)}
            style={{ padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:filterStatus===v?700:400, border:"1.5px solid "+(filterStatus===v?deptColor:"#e5e7eb"), background:filterStatus===v?deptColor+"18":"#fff", color:filterStatus===v?deptColor:"#6b7280", cursor:"pointer", fontFamily:"inherit" }}>{l}
          </button>
        ))}
        <input style={{ flex:1, minWidth:140, padding:"4px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
          placeholder="Search…" value={search} onChange={(e)=>setSearch(e.target.value)}/>
      </div>

      {/* Task list */}
      {filtered.length === 0
        ? <div style={{ background:"#fff", borderRadius:10, border:"2px dashed #e5e7eb", padding:40, textAlign:"center" }}>
            <div style={{ fontSize:36, marginBottom:10 }}>{deptIcon}</div>
            <div style={{ fontWeight:600, marginBottom:6 }}>No tasks in {deptLabel}</div>
            <div style={{ fontSize:12, color:"#9ca3af", marginBottom:14 }}>Add your first task for this department</div>
            <button style={S2.btn(deptColor)} onClick={openAdd}>+ Add First Task</button>
          </div>
        : <div>
            {filtered.map((t) => (
              <TaskCard key={t._id} task={t} members={members} onEdit={openEdit} onStatusChange={changeStatus} onDelete={deleteTask} onCheckItem={checkItem}/>
            ))}
          </div>
      }
    </div>
  );
}

export default function PageTasks({ toast, tenant }) {
  const [tab,       setTab]       = useState("today");
  const [tasks,     setTasks]     = useState([]);
  const [stats,     setStats]     = useState(null);
  const [members,   setMembers]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTask,  setEditTask]  = useState(null);

  // filters
  const [filterRole,     setFilterRole]     = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterType,     setFilterType]     = useState("all");
  const [filterStatus,   setFilterStatus]   = useState("all");
  const [filterCat,      setFilterCat]      = useState(DEPARTMENTS[0]?.label || "Finance & Accounting");
  const [search,         setSearch]         = useState("");
  const [view,           setView]           = useState("list"); // list | kanban

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = tab === "today" ? "/tasks/today" : "/tasks";
      const params   = new URLSearchParams();
      if (tab !== "today" && filterType  !== "all") params.set("type",   filterType);
      if (tab !== "today" && filterStatus !== "all") params.set("status", filterStatus);
      const qs = params.toString() ? "?" + params.toString() : "";

      // Fetch tasks and stats safely — don't crash if one fails
      const [taskData, statsData, memberData] = await Promise.all([
        api("/" + endpoint.slice(1) + qs).catch(() => ({ tasks: [] })),
        api("/tasks/stats").catch(() => ({ all:0, done:0, pending:0, urgent:0, doneToday:0 })),
        api("/team/members").catch(() => ({ members: [] })),
      ]);

      setTasks(taskData.tasks || []);
      setStats(statsData);
      setMembers(memberData.members || []);
    } catch(e) {
      console.error("Tasks load error:", e.message);
      setTasks([]);
    }
    setLoading(false);
  }, [tab, filterType, filterStatus]);

  // Reset status filter when switching tabs
  useEffect(() => {
    if (tab !== "departments") setFilterStatus("all");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const saveTask = async (form) => {
    const payload = { ...form, checklist: form.checklist };
    if (editTask) {
      await api("/tasks/"+editTask._id, { method:"PUT", body:JSON.stringify(payload) });
      toast("Task updated ✓","success");
    } else {
      await api("/tasks", { method:"POST", body:JSON.stringify({ ...payload, createdBy: tenant?.storeName||"Admin" }) });
      toast("Task created ✓","success");
    }
    setEditTask(null);
    load();
  };

  const changeStatus = async (id, status) => {
    await api("/tasks/"+id, { method:"PUT", body:JSON.stringify({ status, completedAt: status==="done"?new Date():null }) });
    toast(status==="done"?"✅ Marked done!":"Status updated","success");
    load();
  };

  const deleteTask = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    await api("/tasks/"+id, { method:"DELETE" });
    toast("Deleted","success");
    load();
  };

  const checkItem = async (taskId, idx) => {
    await api("/tasks/"+taskId+"/check/"+idx, { method:"PUT" });
    load();
  };

  const openAdd  = ()     => { setEditTask(null);  setShowModal(true); };
  const openEdit = (task) => { setEditTask(task);  setShowModal(true); };

  // filter tasks
  const filtered = tasks.filter((t) => {
    if (filterRole!=="all"     && t.assignedRole!==filterRole && t.assignedRole!=="all") return false;
    if (filterPriority!=="all" && t.priority!==filterPriority) return false;
    if (search) { const q=search.toLowerCase(); return t.title.toLowerCase().includes(q)||(t.description||"").toLowerCase().includes(q)||(t.assignedTo||"").toLowerCase().includes(q); }
    return true;
  });

  // kanban columns
  const kanbanCols = STATUSES.map((s)=>({ ...s, tasks:filtered.filter((t)=>t.status===s.id) }));

  // today stats
  const todayDone    = tasks.filter((t)=>t.status==="done").length;
  const todayPending = tasks.filter((t)=>t.status==="pending"||t.status==="in_progress").length;
  const todayUrgent  = tasks.filter((t)=>t.priority==="urgent"&&t.status!=="done").length;

  return (
    <div>
      {/* header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>✅ Tasks & Planner</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Daily · Weekly · Monthly tasks assigned by role</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          {tab!=="today" && (
            <>
              <button style={{ ...S.btnOut, fontSize:12, background:view==="list"?"#f0f9ff":"#fff", borderColor:view==="list"?"#2563eb":"#d1d5db", color:view==="list"?"#2563eb":"#374151" }} onClick={()=>setView("list")}>☰ List</button>
              <button style={{ ...S.btnOut, fontSize:12, background:view==="kanban"?"#f0f9ff":"#fff", borderColor:view==="kanban"?"#2563eb":"#d1d5db", color:view==="kanban"?"#2563eb":"#374151" }} onClick={()=>setView("kanban")}>⊞ Kanban</button>
            </>
          )}
          <button style={S.btn()} onClick={openAdd}>+ New Task</button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
        {[
          { label:"Total Tasks",   value:stats?.all||0,       color:"#1A3A5C", icon:"📋" },
          { label:"Today Done",    value:todayDone||stats?.doneToday||0, color:"#15803d", icon:"✅" },
          { label:"Pending",       value:stats?.pending||0,   color:"#d97706", icon:"⏳" },
          { label:"Urgent",        value:stats?.urgent||0,    color:"#dc2626", icon:"🔴" },
          { label:"Completed All", value:stats?.done||0,      color:"#6366f1", icon:"🏆" },
        ].map((k)=>(
          <div key={k.label} style={{ ...S.card, padding:"12px 14px", marginBottom:0, borderTop:"3px solid "+k.color }}>
            <div style={{ fontSize:16, marginBottom:4 }}>{k.icon}</div>
            <div style={{ fontSize:10, color:"#6b7280", marginBottom:2 }}>{k.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{loading?"—":k.value}</div>
          </div>
        ))}
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16, flexWrap:"wrap" }}>
        {[["today","📅 Today"],["departments","🏢 By Department"],["all","📋 All Tasks"],["daily","🔁 Daily"],["weekly","📅 Weekly"],["monthly","🗓️ Monthly"],["once","1️⃣ One-time"]].map(([id,label])=>(
          <button key={id} style={S.tab(tab===id)} onClick={()=>setTab(id)}>{label}</button>
        ))}
      </div>

      {/* ══ DEPARTMENTS TAB ══ */}
      {tab === "departments" && (
        <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
          {/* Left sidebar — department list */}
          <div style={{ width:220, flexShrink:0, background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
            <div style={{ padding:"10px 14px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:12, color:"#6b7280", textTransform:"uppercase", letterSpacing:"0.5px" }}>Departments</div>
            {DEPARTMENTS.map((dept) => {
              const count = tasks.filter((t) => t.category === dept.label).length;
              const pending = tasks.filter((t) => t.category === dept.label && (t.status==="pending"||t.status==="in_progress")).length;
              return (
                <div key={dept.id} onClick={() => setFilterCat(dept.label)}
                  style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 14px", cursor:"pointer", background:filterCat===dept.label?dept.color+"12":"#fff", borderLeft:"3px solid "+(filterCat===dept.label?dept.color:"transparent"), transition:"all .12s" }}
                  onMouseEnter={(e) => { if(filterCat!==dept.label) e.currentTarget.style.background="#f9fafb"; }}
                  onMouseLeave={(e) => { if(filterCat!==dept.label) e.currentTarget.style.background="#fff"; }}>
                  <span style={{ fontSize:16 }}>{dept.icon}</span>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontSize:12, fontWeight:filterCat===dept.label?700:400, color:filterCat===dept.label?dept.color:"#374151", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{dept.label}</div>
                    {count > 0 && <div style={{ fontSize:10, color:"#9ca3af" }}>{pending} pending · {count} total</div>}
                  </div>
                  {count > 0 && <span style={{ background:dept.color, color:"#fff", borderRadius:20, padding:"1px 7px", fontSize:10, fontWeight:700, flexShrink:0 }}>{count}</span>}
                </div>
              );
            })}
            <div style={{ padding:"8px 14px", borderTop:"1px solid #e5e7eb" }}>
              <button style={{ ...S.btn(), width:"100%", fontSize:12, padding:"7px" }} onClick={openAdd}>+ New Task</button>
            </div>
          </div>

          {/* Right — task list for selected department */}
          <DeptPanel
            filterCat={filterCat}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            search={search}
            setSearch={setSearch}
            tasks={tasks}
            members={members}
            openAdd={openAdd}
            openEdit={openEdit}
            changeStatus={changeStatus}
            deleteTask={deleteTask}
            checkItem={checkItem}
            setEditTask={setEditTask}
            setShowModal={setShowModal}
          />
        </div>
      )}

      {/* Today tab — special layout */}
      {tab === "today" && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:14 }}>
            {[
              { label:"Done today",    value:todayDone,    color:"#15803d", bg:"#f0fdf4" },
              { label:"Still pending", value:todayPending, color:"#d97706", bg:"#fffbeb" },
              { label:"Urgent tasks",  value:todayUrgent,  color:"#dc2626", bg:"#fef2f2" },
            ].map((k)=>(
              <div key={k.label} style={{ background:k.bg, borderRadius:8, padding:"12px 16px", borderLeft:"4px solid "+k.color }}>
                <div style={{ fontSize:11, color:k.color, fontWeight:600, marginBottom:2 }}>{k.label}</div>
                <div style={{ fontSize:28, fontWeight:700, color:k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* role filter pills */}
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap" }}>
            {ROLES.map((r)=>(
              <button key={r.id} onClick={()=>setFilterRole(r.id)} style={{ padding:"5px 14px", borderRadius:20, border:"1.5px solid "+(filterRole===r.id?r.color:"#e5e7eb"), background:filterRole===r.id?r.bg:"#fff", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:filterRole===r.id?700:400, color:r.color }}>
                {r.label}
              </button>
            ))}
            <input style={{ flex:1, minWidth:160, padding:"5px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} placeholder="Search tasks…" value={search} onChange={(e)=>setSearch(e.target.value)}/>
          </div>

          {loading ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading…</div>
          : filtered.length===0
          ? <div style={{ textAlign:"center", padding:60 }}>
              <div style={{ fontSize:48, marginBottom:12 }}>🎉</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>All clear for today!</div>
              <div style={{ fontSize:13, color:"#9ca3af", marginBottom:16 }}>No pending tasks. Add new tasks or check other tabs.</div>
              <button style={S.btn()} onClick={openAdd}>+ Add Task</button>
            </div>
          : <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:12 }}>
              {filtered.map((t)=>(
                <TaskCard key={t._id} task={t} members={members} onEdit={openEdit} onStatusChange={changeStatus} onDelete={deleteTask} onCheckItem={checkItem}/>
              ))}
            </div>
          }
        </>
      )}

      {/* Other tabs */}
      {tab!=="today" && (
        <>
          {/* filters */}
          <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap", alignItems:"center" }}>
            <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} value={filterRole} onChange={(e)=>setFilterRole(e.target.value)}>
              {ROLES.map((r)=><option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} value={filterPriority} onChange={(e)=>setFilterPriority(e.target.value)}>
              <option value="all">All Priorities</option>
              {PRIORITIES.map((p)=><option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            {tab==="all" && (
              <select style={{ padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)}>
                <option value="all">All Status</option>
                {STATUSES.map((s)=><option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            )}
            <input style={{ flex:1, minWidth:160, padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12 }} placeholder="Search tasks…" value={search} onChange={(e)=>setSearch(e.target.value)}/>
            <span style={{ fontSize:12, color:"#6b7280" }}>{filtered.length} tasks</span>
          </div>

          {loading ? <div style={{ textAlign:"center", padding:40, color:"#9ca3af" }}>Loading…</div>
          : filtered.length===0
          ? <div style={{ textAlign:"center", padding:60 }}>
              <div style={{ fontSize:48, marginBottom:12 }}>📋</div>
              <div style={{ fontWeight:700, fontSize:16, marginBottom:6 }}>No tasks yet</div>
              <button style={S.btn()} onClick={openAdd}>+ Create First Task</button>
            </div>
          : view==="list"
          ? (
              <div style={S.card}>
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse" }}>
                    <thead><tr>
                      {["Task","Type","Priority","Assigned To","Category","Due","Status","Progress",""].map((h)=><th key={h} style={S.th}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {filtered.map((t)=>{
                        const pri  = priorityInfo(t.priority);
                        const role = roleInfo(t.assignedRole);
                        const st   = statusInfo(t.status);
                        const over = isOverdue(t);
                        const done = t.checklist.filter((c)=>c.done).length;
                        const total= t.checklist.length;
                        return (
                          <tr key={t._id} style={{ background:over&&t.status!=="done"?"#fff5f5":"" }}
                            onMouseEnter={(e)=>e.currentTarget.style.background="#fafafa"}
                            onMouseLeave={(e)=>e.currentTarget.style.background=over&&t.status!=="done"?"#fff5f5":""}>
                            <td style={{ ...S.td, maxWidth:220 }}>
                              <div style={{ fontWeight:600, fontSize:12, textDecoration:t.status==="done"?"line-through":"none", color:t.status==="done"?"#9ca3af":"#111" }}>{t.title}</div>
                              {t.description && <div style={{ fontSize:10, color:"#9ca3af", marginTop:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.description}</div>}
                              {over && <span style={{ fontSize:9, color:"#dc2626", fontWeight:700 }}>⚠️ OVERDUE</span>}
                            </td>
                            <td style={S.td}><Badge label={TYPES.find((tp)=>tp.id===t.type)?.label||t.type} color="#6b7280" bg="#f3f4f6"/></td>
                            <td style={S.td}><Badge label={pri.label} color={pri.color} bg={pri.bg}/></td>
                            <td style={S.td}>
                              <Badge label={role.label} color={role.color} bg={role.bg}/>
                              {t.assignedTo && <div style={{ fontSize:10, color:"#6b7280", marginTop:2 }}>{t.assignedTo}</div>}
                            </td>
                            <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{t.category}</td>
                            <td style={{ ...S.td, fontSize:11, color:over?"#dc2626":"#6b7280", whiteSpace:"nowrap" }}>
                              {t.dueDate ? new Date(t.dueDate).toLocaleDateString("en-IN") : t.repeatDay ? (t.type==="weekly"?t.repeatDay.charAt(0).toUpperCase()+t.repeatDay.slice(1):"Day "+t.repeatDay) : "—"}
                            </td>
                            <td style={S.td}>
                              <select value={t.status} onChange={(e)=>changeStatus(t._id,e.target.value)}
                                style={{ fontSize:10, padding:"2px 6px", borderRadius:5, border:"1px solid #d1d5db", fontFamily:"inherit", background:st.bg, color:st.color, fontWeight:600, cursor:"pointer" }}>
                                {STATUSES.map((s)=><option key={s.id} value={s.id}>{s.label}</option>)}
                              </select>
                            </td>
                            <td style={S.td}>
                              {total>0 ? (
                                <div>
                                  <div style={{ background:"#f3f4f6", borderRadius:10, height:5, width:80, marginBottom:2 }}>
                                    <div style={{ background:"#22c55e", borderRadius:10, height:5, width:(done/total*80)+"px" }}/>
                                  </div>
                                  <span style={{ fontSize:9, color:"#6b7280" }}>{done}/{total}</span>
                                </div>
                              ) : <span style={{ fontSize:10, color:"#d1d5db" }}>—</span>}
                            </td>
                            <td style={S.td}>
                              <div style={{ display:"flex", gap:4 }}>
                                <button style={{ ...S.btnOut, padding:"2px 7px", fontSize:10 }} onClick={()=>openEdit(t)}>Edit</button>
                                <button style={{ ...S.btnOut, padding:"2px 7px", fontSize:10, color:"#dc2626", borderColor:"#fecaca" }} onClick={()=>deleteTask(t._id)}>Del</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          : (
            /* KANBAN VIEW */
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, alignItems:"start" }}>
              {kanbanCols.map((col)=>(
                <div key={col.id}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                    <span style={{ background:col.bg, color:col.color, padding:"3px 10px", borderRadius:10, fontSize:11, fontWeight:700 }}>{col.label}</span>
                    <span style={{ fontSize:11, color:"#9ca3af" }}>{col.tasks.length}</span>
                  </div>
                  {col.tasks.map((t)=>(
                    <TaskCard key={t._id} task={t} members={members} onEdit={openEdit} onStatusChange={changeStatus} onDelete={deleteTask} onCheckItem={checkItem}/>
                  ))}
                  {col.tasks.length===0 && (
                    <div style={{ background:"#f9fafb", borderRadius:8, padding:"20px 14px", textAlign:"center", border:"2px dashed #e5e7eb" }}>
                      <div style={{ fontSize:10, color:"#d1d5db" }}>No tasks</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {showModal && (
        <TaskModal task={editTask} members={members} onSave={saveTask} onClose={()=>{setShowModal(false);setEditTask(null);}} toast={toast}/>
      )}
    </div>
  );
}
